import React from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Fade,
  Container,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddToCartButton from '../components/AddToCartButton';
import {
  AddShoppingCart,
  Delete,
  FavoriteOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';
import { colors } from '../utils/constants';
import { getDefaultCaseQuantity, getCaseQuantityDisplayText } from '../utils/caseQuantity';

const FavoritesPage: React.FC = () => {
  const navigate = useNavigate();
  const { favorites, loading, removeFromFavorites } = useFavorites();
  const { addToCart } = useCart();
  const theme = useTheme();

  const handleRemoveFromFavorites = async (favoriteId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    await removeFromFavorites(favoriteId);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 700,
              color: 'primary.main',
              mb: 1
            }}
          >
            My Favorites
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Your saved products for easy reordering.
          </Typography>
        </Box>

        {/* Favorites Count */}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {favorites.length} favorite product{favorites.length !== 1 ? 's' : ''}
        </Typography>

        {/* Favorites Grid */}
        {favorites.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 3,
            }}
          >
            {favorites.map((favorite, index) => (
              <Fade in timeout={300 + index * 100} key={favorite.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    '&:hover': {
                      boxShadow: (theme) => theme.shadows[8],
                    },
                  }}
                  onClick={() => navigate(`/products/${favorite.monorepo_reusable_type_id}`)}
                >
                  {/* Product Image Container */}
                  <Box 
                    sx={{
                      height: 200,
                      p: 2,
                      bgcolor: 'grey.50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={favorite.product_snapshot.image_url || '/placeholder-product.png'}
                      alt={favorite.product_name}
                      sx={{
                        objectFit: 'contain',
                        height: '100%',
                        width: 'auto',
                        maxWidth: '100%',
                      }}
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder-product.png';
                      }}
                    />
                    
                    {/* Remove from favorites button */}
                    <Tooltip title="Remove from favorites">
                      <IconButton
                        onClick={(e) => handleRemoveFromFavorites(favorite.id, e)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                          color: colors.primary,
                          '&:hover': {
                            bgcolor: 'background.paper',
                            transform: 'scale(1.1)',
                          },
                          boxShadow: 1,
                        }}
                        size="small"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ p: 0 }}>
                      {/* Product Name */}
                      <Typography variant="h6" fontWeight="bold" sx={{ px: 2, pt: 2 }}>
                        {favorite.product_name}
                      </Typography>

                      {/* Product SKU */}
                      {favorite.product_sku && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          SKU: {favorite.product_sku}
                        </Typography>
                      )}

                      {/* Added Date */}
                      <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1, fontStyle: 'italic' }}>
                        Added {new Date(favorite.created_at).toLocaleDateString()}
                      </Typography>
                    </CardContent>

                    <CardActions sx={{ mt: 'auto', p: 0 }}>
                      <AddToCartButton product={favorite.product_snapshot} />
                    </CardActions>
                  </Box>
                </Card>
              </Fade>
            ))}
          </Box>
        ) : (
          /* Empty State */
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 2,
            }}
          >
            <FavoriteOutlined sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
              No favorites yet
            </Typography>
            <Typography variant="body1" color="text.disabled" sx={{ mb: 3 }}>
              Start adding products to your favorites by clicking the heart icon on product cards
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/products')}
              sx={{
                fontWeight: 600,
                bgcolor: colors.primary,
                '&:hover': {
                  backgroundColor: colors.primary,
                }
              }}
            >
              Browse Products
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default FavoritesPage;
